import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Swiper from "swiper";
import classNames from "classnames";

import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import IconPrevious from "app/pages/.shared/static/icons/IconPrevious";
import IconNext from "app/pages/.shared/static/icons/IconNext";
import IconSliderPause from "app/pages/.shared/static/icons/IconSliderPause";
import IconSliderPlay from "app/pages/.shared/static/icons/IconSliderPlay";
import SmartDPHeroCommercial from "app/pages/SmartDP/SmartDPHeroCommercial/SmartDPHeroCommercial";
import { useAnalytics } from "app/utils/analytics/useAnalytics";

import "./SmartDPHeroCommercialCarousel.scss";

const SmartDPHeroCommercialCarousel = ({
	merchandisings = [],
	autoplayDelay = 5000,
	className,
}) => {
	const { resolution } = useContext(AppGlobalsContext);
	const { track } = useAnalytics();

	const isMobile = resolution === RESOLUTION.SMALL;

	const swiperElRef = useRef(null);
	const swiperRef = useRef(null);
	const holdStateRef = useRef({ active: false, wasPlaying: false });

	const [activeIndex, setActiveIndex] = useState(0);
	const [isPlaying, setIsPlaying] = useState(true);

	const totalSlides = merchandisings.length;

	useEffect(() => {
		if (!swiperElRef.current || totalSlides === 0) {
			return undefined;
		}

		swiperRef.current = new Swiper(swiperElRef.current, {
			speed: 700,
			loop: true,
			slidesPerView: 1,
			spaceBetween: 0,
			threshold: 5,
			shortSwipes: true,
			autoplay: {
				delay: autoplayDelay,
				disableOnInteraction: false,
			},
			on: {
				slideChange: function() {
					setActiveIndex(this.realIndex ?? this.activeIndex ?? 0);
				},
			},
		});

		return () => {
			try {
				if (swiperRef.current) {
					swiperRef.current.destroy();
				}
			} catch (e) {
				// no-op: avoid breaking unmount if Swiper is already destroyed
			}
			swiperRef.current = null;
		};
	}, [autoplayDelay, totalSlides]);

	useEffect(() => {
		if (!swiperRef.current?.autoplay) {
			return;
		}

		if (isPlaying) {
			swiperRef.current.autoplay.start();
		} else {
			swiperRef.current.autoplay.stop();
		}
	}, [isPlaying]);

	const handleTogglePlay = useCallback(() => {
		setIsPlaying(prev => !prev);
	}, []);

	const handlePrev = useCallback(() => {
		track("hero-commercial-carousel-prev-mouse-click");
		if (swiperRef.current) {
			swiperRef.current.slidePrev();
		}
	}, []);

	const handleNext = useCallback(() => {
		track("hero-commercial-carousel-next-mouse-click");
		if (swiperRef.current) {
			swiperRef.current.slideNext();
		}
	}, []);

	const handleGoToSlide = useCallback(index => {
		if (!swiperRef.current) {
			return;
		}

		// With `loop: true`, slide indexes are shifted because Swiper duplicates slides.
		// We target the "real" index by offsetting with `loopedSlides` when available.
		const looped = swiperRef.current.loopedSlides || 0;
		swiperRef.current.slideTo(looped + index);
	}, []);

	const handleNextMouseEnter = useCallback(() => {
		track("hero-commercial-carousel-next-mouse-hover");
	}, []);

	const handlePrevMouseEnter = useCallback(() => {
		track("hero-commercial-carousel-prev-mouse-hover");
	}, []);

	const handleContextMenu = useCallback(e => {
		// Prevent mobile long-press context menu / iOS touch callout inside the carousel.
		e.preventDefault();
	}, []);

	const handleTouchStart = useCallback(() => {
		if (!isMobile) {
			return;
		}
		console.log("handleTouchStart");

		holdStateRef.current.active = true;
		holdStateRef.current.wasPlaying = isPlaying;

		// Pause while the user is holding the slider.
		setIsPlaying(false);
		if (swiperRef.current?.autoplay && typeof swiperRef.current.autoplay.stop === "function") {
			swiperRef.current.autoplay.stop();
		}
	}, [isMobile, isPlaying]);

	const handleTouchEnd = useCallback(() => {
		if (!isMobile || !holdStateRef.current.active) {
			return;
		}

		holdStateRef.current.active = false;

		// Resume only if it was playing before the hold.
		if (holdStateRef.current.wasPlaying) {
			setIsPlaying(true);
		}
	}, [isMobile]);

	const sliderStyle = useMemo(
		() => ({
			"--hcc-autoplay-delay": `${autoplayDelay}ms`,
		}),
		[autoplayDelay]
	);

	const playStateKey = isPlaying ? "play" : "pause";

	return (
		<div
			className={classNames("smartDP-hero-commercial-carousel", className)}
			data-testid="smartDPhero-commercial-carousel"
		>
			{!isMobile && (
				<>
					<button
						type="button"
						className="smartDP-hero-commercial-carousel__nav-button smartDP-hero-commercial-carousel__nav-button--prev flip-if-rlt"
						onClick={handlePrev}
						aria-label="Previous slide"
					>
						<IconPrevious
							className="smartDP-hero-commercial-carousel__nav-icon"
							onMouseEnter={handlePrevMouseEnter}
						/>
					</button>

					<button
						type="button"
						className="smartDP-hero-commercial-carousel__nav-button smartDP-hero-commercial-carousel__nav-button--next flip-if-rlt"
						onClick={handleNext}
						aria-label="Next slide"
					>
						<IconNext
							className="smartDP-hero-commercial-carousel__nav-icon"
							onMouseEnter={handleNextMouseEnter}
						/>
					</button>
				</>
			)}
			<div
				ref={swiperElRef}
				className="swiper-container smartDP-hero-commercial-carousel__swiper"
				onContextMenu={handleContextMenu}
				{...isMobile && {
					onTouchStart: handleTouchStart,
					onTouchEnd: handleTouchEnd,
					onTouchCancel: handleTouchEnd,
				}}
			>
				<div className="swiper-wrapper">
					{merchandisings.map(merch => (
						<div
							className="swiper-slide"
							key={merch?.code || merch?.id || merch?.title}
						>
							<SmartDPHeroCommercial heroCommercialMerch={merch} />
						</div>
					))}
				</div>
			</div>

			{totalSlides > 1 && (
				<div className="smartDP-hero-commercial-carousel__controls" style={sliderStyle}>
					<div className="smartDP-hero-commercial-carousel__slider">
						{!isMobile && (
							<button
								type="button"
								className="smartDP-hero-commercial-carousel__control"
								onClick={handleTogglePlay}
								aria-pressed={!isPlaying}
								aria-label={isPlaying ? "Pause carousel" : "Play carousel"}
							>
								{isPlaying ? (
									<IconSliderPause className="smartDP-hero-commercial-carousel__control-icon" />
								) : (
									<IconSliderPlay className="smartDP-hero-commercial-carousel__control-icon" />
								)}
							</button>
						)}

						<div
							className="smartDP-hero-commercial-carousel__indicators"
							aria-hidden="true"
						>
							{Array.from({ length: totalSlides }).map((_, index) => (
								<span
									key={`indicator-${index}`}
									className={classNames(
										"smartDP-hero-commercial-carousel__indicator",
										{
											"smartDP-hero-commercial-carousel__indicator--active":
												index === activeIndex,
											"smartDP-hero-commercial-carousel__indicator--paused": !isPlaying,
										}
									)}
									onClick={() => handleGoToSlide(index)}
								>
									<span
										key={`fill-${index}-${activeIndex}-${playStateKey}`}
										className="smartDP-hero-commercial-carousel__indicator-fill"
									/>
								</span>
							))}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

SmartDPHeroCommercialCarousel.propTypes = {
	merchandisings: PropTypes.arrayOf(PropTypes.object),
	autoplayDelay: PropTypes.number,
	className: PropTypes.string,
};

export default memo(SmartDPHeroCommercialCarousel);
